import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { DbService } from '../../../../services/db.service';

interface OrganizationStats {
  // Dashboard data (always present)
  total_workflows: number;
  total_shared_workflows: number;
  total_users: number;
  total_submissions: number;
  
  // Usage data (filtered by date)
  total_completions: number;
  child_stats: Array<{
    account_name: string;
    account_id: string;
    completions: number;
    workflow_count: number;
    shared_workflow_count: number;
    user_count: number;
    submission_count: number;
  }>;
  workflow_stats: Array<{
    workflow_name: string;
    workflow_id: string;
    completions: number;
  }>;
}

@Component({
    selector: 'app-analytics',
    templateUrl: './analytics.component.html',
    styleUrls: ['./analytics.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AnalyticsComponent implements OnInit {
  isMaster = false;
  loading = true;
  stats: OrganizationStats | null = null;
  filterStartEnd: Date[] = [];
  tabIndex = 0; // 0 = Dashboard, 1 = Usage

  constructor(
    private dbService: DbService
  ) {
    // Default to last 30 days
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    this.filterStartEnd = [startDate, endDate];
  }

  async ngOnInit() {
    await this.checkMasterStatus();
    if (this.isMaster) {
      await this.loadStats();
    } else {
      this.loading = false;
    }
  }

  onDashboardTab() {
    this.tabIndex = 0;
  }

  onUsageTab() {
    this.tabIndex = 1;
  }

  private async checkMasterStatus(): Promise<void> {
    try {
      const accountData = await this.dbService.getAccountData();
      this.isMaster = accountData?.data?.accountData?.organization?.is_master === true;
    } catch (error) {
      console.error('Failed to check master status:', error);
      this.isMaster = false;
    }
  }

  async loadStats(): Promise<void> {
    if (!this.filterStartEnd || this.filterStartEnd.length !== 2) {
      return;
    }

    this.loading = true;
    try {
      const startDate = new Date(this.filterStartEnd[0]);
      const endDate = new Date(this.filterStartEnd[1]);
      
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      
      this.stats = await this.dbService.getOrganizationStats(
        startDate.toISOString(),
        endDate.toISOString()
      );
    } catch (error) {
      console.error('Failed to load organization stats:', error);
    } finally {
      this.loading = false;
    }
  }

  onDateRangeChange() {
    if (this.filterStartEnd?.length === 2) {
      this.loadStats();
    }
  }

  clearDateFilters() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    this.filterStartEnd = [startDate, endDate];
    this.loadStats();
  }
}
